import { Button, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { useEffect, useState, Suspense, lazy, useCallback } from "react";
import BlockUi from "react-block-ui";
import { withTranslation } from "react-i18next";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import ProfileService from "../../../api/ProfileService";
import {
  getDirectoryCategoryV1,
  getSubscriptionList,
  getSubscriptionStatus,
  getUserService,
  getUserSlots,
} from "../../../redux/actions/calendarAction";
import {
  getAllChatFriendsList,
  getAllCommonLanguageList,
  needPhotVerification,
} from "../../../redux/actions/commonApiActions";
import { updateSocialMediaDetails } from "../../../redux/actions/loginDetailsActions";
import {
  giveUserAction,
  instaValidation,
  linkedInvalidation,
  twitterValidation,
  youtubeValidation,
} from "../../../utils";
import {
  FaInstagram,
  FaYoutube,
  FaLinkedin,
  FaTwitter,
  FaMapMarkerAlt,
  FaGlobe,
  FaUserEdit,
} from "react-icons/fa";

// ==========================================
// OPTIMIZATION 1: LAZY LOADING COMPONENTS
// ==========================================
// Default Exports
const ProfileForm = lazy(() => import("./ProfileForm"));
const CalendarPreference = lazy(() => import("./CalendarPreference"));
const SubscriptionList = lazy(() => import("./SubscriptionList"));
const Services = lazy(() => import("./Services"));
const BankDetails = lazy(() => import("./BankDetails"));
const ServiceDashboard = lazy(() => import("./ServiceDashBoard"));
const Pages = lazy(() => import("./Pages"));
const LaunchServiceSteps = lazy(() => import("./LaunchServiceSteps"));
const ClientsPage = lazy(() => import("./ClientsPage"));
const BookingsPage = lazy(() => import("./BookingsPage"));

// Named Exports (Requires a special syntax to extract the named component)
const WeeklyCalendar = lazy(() =>
  import("../../FirstTimeProfile/WeeklyCalendar").then((module) => ({
    default: module.WeeklyCalendar,
  }))
);
const ServiceProviderFeedbackPage = lazy(() =>
  import("../../FirstTimeProfile/ServiceProviderFeedBackContainer/ServiceProviderFeedbackPage").then((module) => ({
    default: module.ServiceProviderFeedbackPage,
  }))
);

const CalendarContainer = (props) => {
  const { t } = props;
  const dispatch = useDispatch();

  // FIXED: merged into ONE destructure from state.event — previously
  // `subscriptionStatus` was declared twice via two separate useSelector
  // calls, which is a duplicate-identifier syntax error.
  const {
    userSlots,
    eventLoader,
    subscriptionStatus,
    subscriptionDetails,
    prefComplete,
    calendarComplete,
    serviceComplete,
    categoryV2,
    userServiceList,
  } = useSelector((state) => state.event);

  const focusedTb = useSelector(({ dashboard }) => dashboard.focusedTab);
  const { loginDetails } = useSelector((state) => state.loginDetailsApi);
  const { languagelist } = useSelector((state) => state.commonApi);
  const friendsList = useSelector((state) => state.commonApi.chatFriendslist);

  const [menuList, setMenuList] = useState([
    { title: "Profile Details", description: "Add your personal details", id: 1, menuStatus: false },
    { title: "Service Profile", description: "Add Your Service Related Info", id: 2, menuStatus: prefComplete },
    { title: "Suitable Plan", description: "Choose a Suitable Plan For You", id: 3, menuStatus: true },
    { title: "Bank Details", description: "Add your bank details", id: 6, menuStatus: userSlots?.preference?.accountNumber },
  ]);

  const [state, setState] = useState({
    activeMenu: 0,
    loader: false,
    activeWrapperMenu: null,
    biography: "",
    twitter: "",
    location: "",
    youtube: "",
    instagram: "",
    linkedIn: "",
    initialServicePage: false,
    websiteLink: "",
    neededTypeRedirect: "",
  });

  const [feedbackConfigModal, setFeedbackConfigModal] = useState(false);
  const [hasShownModal, setHasShownModal] = useState(false);

  useEffect(() => {
    makeAction(focusedTb);
  }, [focusedTb]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const totalItems = menuList.length;
      const completedItems = menuList.filter((item) => item.menuStatus).length;
      const progress = (completedItems / totalItems) * 100;

      if (progress < 100 && !hasShownModal) {
        Swal.fire({
          title: "Update Your Service Profile",
          html: `
            <div style="text-align: center;">
              <img src="https://corporate.yuukke.com/bcav2/auth/image/d14916c9-8f4e-4778-a5d7-5b33819460c5"
                   alt="Profile Update"
                   style="width: 65px; height: 65px; margin-bottom: 20px; animation: bounce 1s infinite alternate;" />
              <p style="font-size: 18px; font-weight: bold; color: #333;">Boost Your Profile – Update Now!</p>
              <p style="font-size: 16px; color: #666; margin-bottom: 15px;">Your service profile is <strong>${progress.toFixed(2)}% complete</strong>.</p>
              <div style="background: #f8f8f8; border-radius: 12px; padding: 15px; margin: 20px auto; max-width: 300px;">
                <p style="font-size: 14px; color: #444; font-weight: 500;">Progress: <strong>${progress.toFixed(2)}% Complete</strong></p>
                <div style="background: #ddd; border-radius: 6px; height: 12px; width: 100%; position: relative;">
                  <div style="background: #4CAF50; width: ${progress}%; height: 100%; border-radius: 6px;"></div>
                </div>
              </div>
              <p style="font-size: 14px; color: #888; margin-top: 10px;">A complete profile increases your visibility! ✨</p>
            </div>
          `,
          confirmButtonText: "Got it",
          confirmButtonColor: "#4CAF50",
          allowOutsideClick: true,
        }).then(() => {
          setHasShownModal(true);
        });
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [menuList, hasShownModal , subscriptionStatus]);

  useEffect(() => {
    setInitialState();
    dispatch(getUserService());
    dispatch(getUserSlots());
    dispatch(getSubscriptionStatus("calender"));
    dispatch(getSubscriptionList());
    if (categoryV2.length === 0) dispatch(getDirectoryCategoryV1());
    if (languagelist.length === 0) dispatch(getAllCommonLanguageList());
    if (friendsList?.length === 0) dispatch(getAllChatFriendsList());
  }, []);

  useEffect(() => {
    if (!state.initialServicePage && serviceComplete && subscriptionStatus && state.activeWrapperMenu !== 5) {
      setState((previous) => ({
        ...previous,
        activeMenu: userServiceList?.length >= 1 ? 7 : 5,
        initialServicePage: true,
      }));
    }
  }, [serviceComplete, subscriptionStatus]);

  useEffect(() => {
    let list = menuList.map((menu) => {
      switch (menu.id) {
        case 2: return { ...menu, menuStatus: prefComplete };
        case 3: return { ...menu, menuStatus: subscriptionStatus };
        case 4: return { ...menu, menuStatus: calendarComplete };
        case 5: return { ...menu, menuStatus: serviceComplete };
        case 6: return { ...menu, menuStatus: userSlots?.preference?.accountNumber };
        default: return menu;
      }
    });
    setMenuList(list);
  }, [
    prefComplete, subscriptionStatus, calendarComplete, serviceComplete,
    userSlots?.preference?.accountNumber, loginDetails.biography, loginDetails.location,
    loginDetails.instagram, loginDetails.linkedIn, loginDetails.twitter, loginDetails.personalWebsite,
  ]);

  const setInitialState = () => {
    setState((previous) => ({
      ...previous,
      biography: loginDetails.biography,
      twitter: loginDetails.twitter,
      location: loginDetails.location,
      instagram: loginDetails.instagram,
      youtube: loginDetails.youtube,
      linkedIn: loginDetails.linkedin,
      websiteLink: loginDetails.personalWebsite,
    }));
    if (loginDetails.biography && loginDetails.location && loginDetails.instagram) {
      updateMenu(1);
    }
  };

  // ==========================================
  // OPTIMIZATION 2: USECALLBACK FOR FUNCTIONS
  // ==========================================
  const updateState = useCallback((name, value) => {
    setState((previous) => ({ ...previous, [name]: value }));
  }, []);

  const makeAction = useCallback((index) => {
    setState((prev) => ({ ...prev, activeMenu: index }));
  }, []);

  const clearNeededType = useCallback(() => {
    setState((prev) => ({ ...prev, neededTypeRedirect: "" }));
  }, []);

  const neededTypeRedirect = useCallback((redirect, index) => {
    setState((prev) => ({ ...prev, neededTypeRedirect: redirect, activeMenu: index }));
  }, []);

  const menuStatusChange = (id, data) => {
    if (giveUserAction(loginDetails?.profile_complete)) {
      dispatch(needPhotVerification(true));
      return;
    }
    const unComplete = !data.menuStatus && menuList.slice(0, 4).find((item) => !item.menuStatus);

    if (unComplete && !data.menuStatus) {
      if (id !== state.activeWrapperMenu && unComplete.id !== id) {
        toast.warning(`Please complete ${unComplete.title} step`);
      }
      setState((previous) => ({
        ...previous,
        activeWrapperMenu: id === state.activeWrapperMenu ? null : unComplete.id,
        initialServicePage: false,
      }));
      return;
    } else if (!subscriptionStatus && id !== 1 && id !== 2) {
      toast.warning("Please subscribe your suitable plan");
      setState((previous) => ({ ...previous, activeWrapperMenu: 3, initialServicePage: false }));
      return;
    }
    setState((previous) => ({
      ...previous,
      activeWrapperMenu: id === state.activeWrapperMenu ? null : id,
    }));
  };

  const removeMenuStatus = useCallback((type, status) => {
    if (status && type) {
      setMenuList((prevList) => prevList.map((item) => {
        if (type === "calendar" && item.id === 2) { item.menuStatus = true; item.title = "preference details"; }
        else if (type === "subscription" && item.id === 3) { item.menuStatus = true; }
        else if (type === "bank" && item.id === 6) { item.menuStatus = true; }
        return item;
      }));
    }
    setState((previous) => ({ ...previous, activeWrapperMenu: null, activeMenu: 0, initialServicePage: false }));
  }, []);

  const updateMenu = useCallback((id) => {
    setMenuList((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            menuStatus: true,
            title: item.title.startsWith("Update Your")
              ? item.title
              : `Update Your ${item.title.replace(/^Add Your |^Complete /, "").trim()}`,
          };
        }
        return item;
      })
    );
  }, []);

  const saveProfileChanges = (e) => {
    e.preventDefault();
    if (!state.biography) {
      toast.warning("Enter your service details");
    } else if (!state.location) {
      toast.warning("Enter your location");
    } else if (!state.instagram && !state.linkedIn && !state.twitter) {
      toast.warning("Enter your social media url");
    } else {
      let validCount = 0;
      const data = { biography: state.biography, location: state.location };
      if (state.websiteLink) data.personalWebsite = state.websiteLink;
      if (linkedInvalidation(state.linkedIn)) { data.linkedin = state.linkedIn; validCount++; }
      if (instaValidation(state.instagram)) { data.instagram = state.instagram; validCount++; }
      if (youtubeValidation(state.youtube)) { data.youtube = state.youtube; validCount++; }
      if (twitterValidation(state.twitter)) { data.twitter = state.twitter; validCount++; }

      if (validCount >= 2) {
        updateState("loader", true);
        validProfileDetails(data);
      } else {
        toast.warning("Please add minimum two of your social media profile url");
      }
    }
  };

  const validProfileDetails = (data) => {
    updateState("loader", true);
    ProfileService.userProfileDetailsUpdated(data)
      .then(async () => {
        await dispatch(updateSocialMediaDetails(data));
        await dispatch(getUserService());
        updateState("loader", false);
        removeMenuStatus();
      })
      .catch(() => updateState("loader", false));
  };

  useEffect(() => {
    const updatedMenuList = menuList.map((item) => {
      let newStatus, newTitle;
      switch (item.id) {
        case 1: newStatus = !!(loginDetails.biography && loginDetails.location && (loginDetails.instagram || loginDetails.linkedIn || loginDetails.twitter)); break;
        case 2: newStatus = prefComplete; break;
        case 3: newStatus = subscriptionStatus; break;
        case 4: newStatus = calendarComplete; break;
        case 6: newStatus = !!userSlots?.preference?.accountNumber; break;
        case 5: newStatus = serviceComplete; break;
        default: newStatus = item.menuStatus;
      }
      newTitle = newStatus
        ? item.title.startsWith("Update Your") ? item.title : `Update Your ${item.title.replace(/^Add Your |^Complete /, "")}`
        : item.title.replace(/^Update Your /, "Add Your ");

      if (item.id === 1) newTitle = newStatus ? "Update Profile Details" : "Complete Profile Details";
      if (item.id === 3) newTitle = newStatus ? "Update Subscription Plan" : "Select Suitable Plan";
      if (item.id === 5) newTitle = newStatus ? "Update Service" : "Create Service";

      return { ...item, menuStatus: newStatus, title: newTitle };
    });
    setMenuList(updatedMenuList);
  }, [prefComplete, subscriptionStatus, calendarComplete, serviceComplete, userSlots, loginDetails]);

  // ==========================================
  // OPTIMIZATION 3: SWITCH RENDER FUNCTION
  // ==========================================
  // FIXED: switch extracted into its own function. A switch statement is
  // a statement, not an expression — it can never be embedded directly
  // inside a JSX `{ }` block the way the original code attempted.
  const renderMenuByActiveTab = () => {
    switch (state.activeMenu) {
      case 1:
        return (
          <ProfileForm
            state={state}
            updateState={updateState}
            saveProfileChanges={saveProfileChanges}
            removeMenuStatus={removeMenuStatus}
            t={t}
          />
        );
      case 2:
        return <CalendarPreference onBack={removeMenuStatus} />;
      case 3:
        return <SubscriptionList onBack={removeMenuStatus} />;
      case 4:
        return (
          <WeeklyCalendar
            onBack={() => removeMenuStatus()}
            addSuccess={updateMenu}
            userSlots={userSlots?.slots}
            makeAction={makeAction}
            from={"service"}
            activeMinutes={30}
            {...props}
          />
        );
      case 5:
        return (
          <Services
            addSuccess={updateMenu}
            onBack={makeAction}
            initialServicePage={state.initialServicePage}
            userPrefDetails={userSlots?.preference}
            neededTypeRedirect={state.neededTypeRedirect}
            clearNeededType={clearNeededType}
            {...props}
          />
        );
      case 6:
        return <BankDetails onBack={removeMenuStatus} userSlots={userSlots?.preference} />;
      case 7:
        return <ServiceDashboard makeAction={makeAction} neededTypeRedirect={neededTypeRedirect} {...props} />;
      case 8:
        return <Pages />;
      case 10:
        return <BookingsPage />;
      case 12:
        return <ClientsPage />;
      case 13:
        return (
          <ServiceProviderFeedbackPage
            providerId={userSlots?.preference?.userId}
            specializationId={userSlots?.preference?.categorys}
          />
        );
      default:
        return (
          <LaunchServiceSteps
            menuList={menuList}
            state={state}
            updateState={updateState}
            menuStatusChange={menuStatusChange}
            makeAction={makeAction}
            from={props.from}
            t={t}
          />
        );
    }
  };

  // FIXED: proper ternary (`condition ? a : b`) instead of the invalid
  // `{!subscriptionStatus : ( ) : ( )}` syntax. When there's no active
  // subscription, show SubscriptionList directly; otherwise render
  // whichever tab is active via the switch function above.
  const renderActiveMenu = () => {
    return !subscriptionStatus ? (
      <SubscriptionList onBack={removeMenuStatus} />
    ) : (
      renderMenuByActiveTab()
    );
  };

  return (
    <BlockUi
      tag="div"
      message="Loading, please wait"
      style={{ minHeight: "134px" }}
      className="wallLoadingPage"
      blocking={state.loader || eventLoader}
    >
      <div className="cal-v2-wrap" style={{ paddingBottom: "2rem" }}>
        {/* OPTIMIZATION 4: SUSPENSE FALLBACK */}
        <Suspense fallback={<div style={{ textAlign: "center", padding: "2rem" }}>Loading...</div>}>
          {renderActiveMenu()}
        </Suspense>
      </div>
    </BlockUi>
  );
};

export default withTranslation()(CalendarContainer);